import { UserRole, PermissionCheck } from './types';

/**
 * Permission Service
 * Core service for checking user permissions and role access
 * Provides centralized permission logic with caching and hierarchy support
 */
export class PermissionService {
  private userRoles: UserRole[] = [];
  private roleHierarchy: Map<UserRole, UserRole[]> = new Map();

  constructor(userRoles: UserRole[] = []) {
    this.userRoles = userRoles;
    this.initializeRoleHierarchy();
  }

  /**
   * Initialize role hierarchy
   * Define which roles inherit permissions from others
   * Example: ADMIN could inherit all other permissions
   */
  private initializeRoleHierarchy(): void {
    // Currently flat structure - can be extended for role hierarchy
    // Example:
    // this.roleHierarchy.set(UserRole.USERS, [UserRole.USER_GROUPS]);
  }

  /**
   * Update user roles
   */
  setUserRoles(roles: UserRole[]): void {
    this.userRoles = roles;
  }

  /**
   * Get current user roles
   */
  getUserRoles(): UserRole[] {
    return [...this.userRoles];
  }

  /**
   * Check if user has ANY of the specified roles (OR logic)
   * This is the primary method matching TIXApp.Web behavior
   */
  hasPermission(roles: UserRole[]): boolean {
    if (!roles || roles.length === 0) return true;
    if (!this.userRoles || this.userRoles.length === 0) return false;

    const result = roles.some((role) => this.userRoles.includes(role));
    return result;
  }

  /**
   * Check if user has ALL of the specified roles (AND logic)
   */
  hasAllPermissions(roles: UserRole[]): boolean {
    if (!roles || roles.length === 0) return true;
    if (!this.userRoles || this.userRoles.length === 0) return false;

    return roles.every((role) => this.userRoles.includes(role));
  }

  /**
   * Flexible permission check supporting both OR and AND logic
   */
  checkPermission(check: PermissionCheck): boolean {
    if (check.requireAll) {
      return this.hasAllPermissions(check.roles);
    }
    return this.hasPermission(check.roles);
  }

  /**
   * Check if user has a specific role
   */
  hasRole(role: UserRole): boolean {
    return this.userRoles.includes(role);
  }

  /**
   * Check if user has any roles at all
   */
  hasAnyRole(): boolean {
    return this.userRoles.length > 0;
  }

  /**
   * Get inherited roles based on hierarchy
   * Returns all roles including inherited ones
   */
  getEffectiveRoles(): UserRole[] {
    const effectiveRoles = new Set(this.userRoles);

    this.userRoles.forEach((role) => {
      const inheritedRoles = this.roleHierarchy.get(role) || [];
      inheritedRoles.forEach((inheritedRole) => effectiveRoles.add(inheritedRole));
    });

    return Array.from(effectiveRoles);
  }

  /**
   * Feature-based permission check
   * Maps feature names to required roles for easier management
   */
  canAccessFeature(feature: string): boolean {
    const featureRoleMap: Record<string, UserRole[]> = {
      dashboard: [], // Public - no roles required
      fixtures: [UserRole.FIXTURES],
      'position-list': [UserRole.POSITION_LIST],
      'cargo-list': [UserRole.CARGO_LIST],
      baltic: [UserRole.BALTIC],
      'bunker-prices': [UserRole.BUNKER_PRICES],
      'cargo-count': [UserRole.CARGO_COUNT],
      'fixed-days': [UserRole.FIXED_DAYS],
      'user-groups': [UserRole.USER_GROUPS],
      'user-management': [UserRole.USERS],
      reports: [UserRole.REPORTS, UserRole.REPORTS_FILES],
      profile: [], // Profile - always accessible
    };

    const requiredRoles = featureRoleMap[feature];
    if (!requiredRoles) return false;
    if (requiredRoles.length === 0) return true; // Public feature

    return this.hasPermission(requiredRoles);
  }

  /**
   * Clear all roles (for logout)
   */
  clearRoles(): void {
    this.userRoles = [];
  }
}

// Export singleton instance
export const permissionService = new PermissionService();
